import React, { useState, useRef, useEffect } from 'react';

import {
    Form,
    Input,
    Radio,
    InputNumber,
    Button,
    Select,
    Modal,
    Table,
    Row,
    Col,
    Space,
    Card
} from 'antd';

import { EnglishToHebrew, HEBREW_PREFIXES } from './HebrewInput';
import { EnglishToGreek, GREEK_PREFIXES, GREEK_SECOND_PREFIXES } from './GreekInput';
import { ToTitleCase, getBookDisplayName } from './Populate';
import { KeyboardReference } from './KeyboardReference';
import { AudioRecorder } from './AudioRecorder';
import { authAxios } from '../../auth';
import { API } from '../../config';
import { FontSizeOutlined } from '@ant-design/icons';

const parsingLOT = {
    partsOfSpeech: [
        "noun",
        "verb",
        "article",
        "preposition",
        "adverb",
        "adjective",
        "conjunction",
        "particle",
        "demonstrative pronoun",
        "interrogative or indefinite pronoun",
        "personal or possessive pronoun",
        "relative pronoun",
    ],
    personAndDeclension: [
        "first",
        "second",
        "third",
    ],
    tense: [
        "aorist",
        "future",
        "imperfect",
        "present",
        "perfect",
        "pluperfect",
    ],
    voice: [
        "active",
        "middle",
        "passive",
    ],
    mood: [
        "imperative",
        "indicative",
        "infinitive",
        "optative",
        "participle",
    ],
    case: [
        "accusative",
        "dative",
        "genitive",
        "nominative",
        "vocative",
    ],
    number: [
        "singular",
        "plural",
    ],
    gender: [
        "feminine",
        "masculine",
        "both", // Hebrew nouns can be both masculine and feminine
        "neuter",
    ],
    degree: [
        "comparative",
        "superlative",
    ],
    hebrewStem: [
        "qal",
        "niphal",
        "piel",
        "pual",
        "hiphil",
        "hophal",
        "hithpael",
    ],
}

const required = [{ required: true }];

const buildSelects = (selects: string[]) => selects.map(s =>
    <Select.Option key={s} value={s}>
        {s.charAt(0).toUpperCase() + s.slice(1)}
    </Select.Option>
)

// Interface for lexical form lookup results
interface LexicalFormMatch {
    word: string;
    lexical_form: string | null;
    gloss: string | null;
    part_of_speech: string | null;
    person: string | null;
    tense: string | null;
    voice: string | null;
    mood: string | null;
    case: string | null;
    number: string | null;
    gender: string | null;
    degree: string | null;
    declension?: string | null;
    state?: string | null;
    stem?: string | null;
    class?: string | null;
}

// Interface for manuscript word data
interface ManuscriptWord {
    chapter: number;
    verse: number;
    position: number;
    word: string;
    is_punctuation?: number;  // 1 if punctuation, 0 or undefined if word
}

interface InputFormProps {
    verses: any[];
    book: string;
    chapter: number;
    verse: number;
    lang: string;
    refreshKey: number;
    setRefreshKey: (key: number) => void;
    editData?: any;
    clearEditData?: () => void;
    isModal?: boolean;
    onClose?: () => void;
    maxPosition?: number; // Maximum position allowed (from exemplar)
    onPositionChange?: (position: number) => void; // Callback when position changes
    manuscriptData?: ManuscriptWord[]; // Current verse manuscript data for punctuation hints
}

// Common punctuation marks by language
const HEBREW_PUNCTUATION = ['׃', '־', ',', '.', ';', ':', '!', '?', '"', "'"];
const GREEK_PUNCTUATION = ['.', ',', ';', ':', '·', '!', '?', '"', "'", '«', '»'];

export const InputForm = (props: InputFormProps) => {
    const [form] = Form.useForm();
    const { verses, book, chapter, verse, lang, refreshKey, setRefreshKey, editData, clearEditData, isModal, onClose, maxPosition, onPositionChange, manuscriptData } = props;
    const [partOfSpeech, setPartOfSpeech] = useState<string>("noun");
    const hebrewPendingRef = useRef<Record<string, string>>({});
    const greekPendingRef = useRef<Record<string, string>>({});
    const [lexicalMatches, setLexicalMatches] = useState<LexicalFormMatch[]>([]);
    const [isLexicalModalVisible, setIsLexicalModalVisible] = useState<boolean>(false);
    const [editId, setEditId] = useState<number | null>(null);
    const [showKeyboard, setShowKeyboard] = useState<boolean>(false);
    const [currentWord, setCurrentWord] = useState<string>('');
    const [currentPosition, setCurrentPosition] = useState<number>(1);

    // Track the last focused translatable input field
    const lastFocusedFieldRef = useRef<{ name: string; element: HTMLInputElement | null }>({ name: '', element: null });
    const cursorPositionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });

    // Populate form when editData changes
    useEffect(() => {
        if (editData) {
            setEditId(editData.id);
            setCurrentWord(editData.word || '');
            if (editData.part_of_speech) {
                setPartOfSpeech(editData.part_of_speech);
            }
            form.setFieldsValue({
                word: editData.word,
                definition_in_context: editData.definition_in_context,
                lexical_form: editData.lexical_form,
                gloss: editData.gloss,
                part_of_speech: editData.part_of_speech,
                declension: editData.declension,
                person: editData.person,
                tense: editData.tense,
                voice: editData.voice,
                mood: editData.mood,
                case: editData.case,
                number: editData.number,
                gender: editData.gender,
                degree: editData.degree,
                state: editData.state,
                stem: editData.stem,
                class: editData.class,
                position: editData.position,
                book: ToTitleCase(book.match(/[^\d]+|\d+/g)?.join(' ')),
                chapter,
                verse,
            });
        } else {
            setEditId(null);
            setCurrentWord('');
        }
    }, [editData, form, book, chapter, verse]);

    const onFinish = (values: any) => {
        values.book = values.book.toLowerCase().replace(/\s/g, '');

        if (editId) {
            values.id = editId;
            authAxios.put(API.updateWord, values, {
                headers: { 'Content-Type': 'application/json' }
            })
                .then(() => {
                    setRefreshKey(refreshKey + 1);
                    form.resetFields();
                    setEditId(null);
                    setCurrentWord('');
                    if (clearEditData) clearEditData();
                    if (onClose) onClose();
                })
                .catch((err: any) => console.error(err));
        } else {
            authAxios.post(API.word, values, {
                headers: { 'Content-Type': 'application/json' }
            })
                .then(() => {
                    setRefreshKey(refreshKey + 1);
                    form.resetFields();
                    setCurrentWord('');
                    if (onClose) onClose();
                })
                .catch((err: any) => console.error(err));
        }
    }

    const handleHebrewKeyDown = (e: any) => {
        const name = e.target.id || e.target.name;
        const key = e.key;

        // Ignore modifier keys - they shouldn't trigger or complete combos
        if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(key)) {
            return;
        }

        if (HEBREW_PREFIXES.includes(key)) {
            e.preventDefault();
            hebrewPendingRef.current[name] = key;
            return;
        }

        const pending = hebrewPendingRef.current[name];
        if (pending) {
            e.preventDefault();
            hebrewPendingRef.current[name] = '';
            const input = e.target;
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const currentValue = form.getFieldValue(name) || '';
            const combo = pending + key;
            const before = currentValue.slice(0, start);
            const after = currentValue.slice(end);
            const newText = before + combo + after;
            const converted = EnglishToHebrew(newText);
            form.setFieldsValue({ [name]: converted });
            setTimeout(() => {
                const newPos = before.length + 1;
                input.setSelectionRange(newPos, newPos);
            }, 0);
            return;
        }
    }

    const handleGreekKeyDown = (e: any) => {
        const name = e.target.id || e.target.name;
        const key = e.key;

        // Ignore modifier keys - they shouldn't trigger or complete combos
        if (['Shift', 'Control', 'Alt', 'Meta', 'CapsLock'].includes(key)) {
            return;
        }

        // If it's a first-level prefix, store it and suppress display
        if (GREEK_PREFIXES.includes(key) && !greekPendingRef.current[name]) {
            e.preventDefault();
            greekPendingRef.current[name] = key;
            return;
        }

        const pending = greekPendingRef.current[name];
        if (pending) {
            // Check if this creates a second-level prefix (e.g., ':' + '/' = ':/')
            if (GREEK_SECOND_PREFIXES.includes(key)) {
                e.preventDefault();
                greekPendingRef.current[name] = pending + key;
                return;
            }

            // Complete the sequence: pending + key
            e.preventDefault();
            greekPendingRef.current[name] = '';
            const input = e.target;
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const currentValue = form.getFieldValue(name) || '';
            const combo = pending + key;
            const before = currentValue.slice(0, start);
            const after = currentValue.slice(end);
            const newText = before + combo + after;
            const converted = EnglishToGreek(newText);
            form.setFieldsValue({ [name]: converted });

            // Track word changes for audio recording
            if (name === 'word') {
                setCurrentWord(converted);
            }

            setTimeout(() => {
                const newPos = before.length + 1;
                input.setSelectionRange(newPos, newPos);
            }, 0);
            return;
        }
    }

    const handleHebrew = (e: any) => {
        const input = e.target;
        const name = input.id;
        const cursorPos = input.selectionStart;
        const value = EnglishToHebrew(input.value);
        form.setFieldsValue({ [name]: value });

        // Track word changes for audio recording
        if (name === 'word') {
            setCurrentWord(value);
        }

        // Restore cursor position after React updates the DOM
        setTimeout(() => {
            input.setSelectionRange(cursorPos, cursorPos);
        }, 0);
    }

    const handleGreek = (e: any) => {
        const input = e.target;
        const name = input.id;
        const cursorPos = input.selectionStart;
        const value = EnglishToGreek(input.value);
        form.setFieldsValue({ [name]: value });

        // Track word changes for audio recording
        if (name === 'word') {
            setCurrentWord(value);
        }

        // Restore cursor position after React updates the DOM
        setTimeout(() => {
            input.setSelectionRange(cursorPos, cursorPos);
        }, 0);
    }

    const handleRadioChange = (e: any) => {
        setPartOfSpeech(e.target.value);
    }

    // Track focus on translatable input fields
    const handleTranslatableInputFocus = (e: React.FocusEvent<HTMLInputElement>, fieldName: string) => {
        lastFocusedFieldRef.current = { name: fieldName, element: e.target };
        cursorPositionRef.current = {
            start: e.target.selectionStart || 0,
            end: e.target.selectionEnd || 0
        };
    };

    // Update cursor position on selection change
    const handleTranslatableInputSelect = (e: React.SyntheticEvent<HTMLInputElement>) => {
        const input = e.target as HTMLInputElement;
        cursorPositionRef.current = {
            start: input.selectionStart || 0,
            end: input.selectionEnd || 0
        };
    };

    // Handle character click from keyboard reference
    const handleKeyboardCharacterClick = (char: string) => {
        const { name, element } = lastFocusedFieldRef.current;
        if (!name) return;

        const currentValue = form.getFieldValue(name) || '';
        const { start, end } = cursorPositionRef.current;

        // Insert character at cursor position
        const before = currentValue.slice(0, start);
        const after = currentValue.slice(end);
        const newValue = before + char + after;

        // Update form field
        form.setFieldsValue({ [name]: newValue });

        // Update cursor position for next insertion
        const newPos = start + char.length;
        cursorPositionRef.current = { start: newPos, end: newPos };

        // Refocus the input and set cursor position
        if (element) {
            setTimeout(() => {
                element.focus();
                element.setSelectionRange(newPos, newPos);
            }, 0);
        }
    };

    const populateFormFromMatch = (match: LexicalFormMatch) => {
        const fieldsToSet: Record<string, any> = {};
        if (match.gloss) fieldsToSet.gloss = match.gloss;
        if (match.part_of_speech) {
            fieldsToSet.part_of_speech = match.part_of_speech;
            setPartOfSpeech(match.part_of_speech);
        }
        if (match.person) fieldsToSet.person = match.person;
        if (match.tense) fieldsToSet.tense = match.tense;
        if (match.voice) fieldsToSet.voice = match.voice;
        if (match.mood) fieldsToSet.mood = match.mood;
        if (match.case) fieldsToSet.case = match.case;
        if (match.number) fieldsToSet.number = match.number;
        if (match.gender) fieldsToSet.gender = match.gender;
        if (match.degree) fieldsToSet.degree = match.degree;
        if (match.declension) fieldsToSet.declension = match.declension;
        if (match.state) fieldsToSet.state = match.state;
        if (match.stem) fieldsToSet.stem = match.stem;
        if (match.class) fieldsToSet.class = match.class;
        form.setFieldsValue(fieldsToSet);
    };

    const handleLexicalFormBlur = () => {
        const lexicalForm = form.getFieldValue("lexical_form");
        if (!lexicalForm || lexicalForm.trim() === "") return;

        const lookupUrl = lang === "hebrew"
            ? API.hebrewByLexicalForm(lexicalForm)
            : API.greekByLexicalForm(lexicalForm);

        authAxios.get(lookupUrl)
            .then((response: { data: LexicalFormMatch[] }) => {
                const matches = response.data;
                if (matches.length === 1) {
                    populateFormFromMatch(matches[0]);
                } else if (matches.length > 1) {
                    setLexicalMatches(matches);
                    setIsLexicalModalVisible(true);
                }
            })
            .catch((error: any) => console.error("Error looking up lexical form:", error));
    };

    const handleMatchSelection = (selectedMatch: LexicalFormMatch) => {
        populateFormFromMatch(selectedMatch);
        setIsLexicalModalVisible(false);
        setLexicalMatches([]);
    };

    const handleCheckWord = () => {
        let word = form.getFieldValue("word");
        const glossaryUrl = lang === "hebrew" ? API.hebrewGlossary(word) : API.greekGlossary(word);

        authAxios.get(glossaryUrl)
            .then((res: { data: any; }) => {
                let resp = res.data;
                if (Object.keys(resp).length > 0) {
                    form.setFieldsValue({
                        lexical_form: resp.lexical_form,
                        gloss: resp.gloss,
                        part_of_speech: resp.part_of_speech,
                        declension: resp.declension,
                        person: resp.person,
                        number: resp.number,
                        gender: resp.gender,
                        case: resp.case,
                        tense: resp.tense,
                        voice: resp.voice,
                        mood: resp.mood,
                    });
                    if (resp.part_of_speech) setPartOfSpeech(resp.part_of_speech);
                }
            }).catch(e => console.log(e));
    };

    const handleCancel = () => {
        form.resetFields();
        setEditId(null);
        setCurrentWord('');
        if (clearEditData) clearEditData();
        if (onClose) onClose();
    };

    // Quick-add punctuation (skips the full form)
    const handleQuickAddPunctuation = (punctuationMark: string) => {
        const values = {
            book: book.toLowerCase().replace(/\s/g, ''),
            chapter,
            verse,
            position: currentPosition,
            word: punctuationMark,
            lexical_form: punctuationMark,
            gloss: 'punctuation',
            part_of_speech: 'punctuation',
            definition_in_context: 'punctuation',
        };

        authAxios.post(API.word, values, {
            headers: { 'Content-Type': 'application/json' }
        })
            .then(() => {
                setRefreshKey(refreshKey + 1);
            })
            .catch((err: any) => console.error(err));
    };

    // Auto-set location values when not in edit mode
    const nextPosition = verses.length > 0 ? verses[verses.length - 1].position + 1 : 1;

    // Check if current position in manuscript is punctuation
    // Use nextPosition directly (not state) to avoid timing issues on initial render
    const effectivePosition = editId ? currentPosition : nextPosition;
    // manuscriptData is already filtered by verse in parent, so just check position
    const currentManuscriptWord = manuscriptData?.find(
        w => w.position === effectivePosition
    );
    const isCurrentPositionPunctuation = currentManuscriptWord?.is_punctuation === 1;
    const expectedPunctuation = isCurrentPositionPunctuation ? currentManuscriptWord?.word : null;

    useEffect(() => {
        if (!editId) {
            setCurrentPosition(nextPosition);
            if (onPositionChange) {
                onPositionChange(nextPosition);
            }
        }
    }, [nextPosition, editId, onPositionChange]);

    useEffect(() => {
        if (editData?.position) {
            setCurrentPosition(editData.position);
            if (onPositionChange) {
                onPositionChange(editData.position);
            }
        }
    }, [editData?.position, onPositionChange]);

    if (!editId) {
        form.setFieldsValue({
            book: ToTitleCase(book.match(/[^\d]+|\d+/g)?.join(' ')),
            chapter,
            verse,
            position: nextPosition,
        });
    }

    // Check if adding is disabled (position exceeds exemplar)
    const isPositionExceeded = maxPosition !== undefined && currentPosition > maxPosition;

    // Check which grammar fields should be visible
    const isVerb = ["verb"].includes(partOfSpeech);
    const showHebrewVerbFields = lang === 'hebrew' && isVerb;
    const showGreekVerbFields = lang === 'greek' && isVerb;
    const showCaseField = lang === 'greek' && ["article", "noun", "verb", "personal_or_possessive_pronoun", "adjective"].includes(partOfSpeech);
    const showNumberField = ["article", "noun", "verb", "personal_or_possessive_pronoun", "adjective"].includes(partOfSpeech);
    const showGenderField = ["article", "noun", "verb", "adjective"].includes(partOfSpeech);
    const showDegreeField = ["adverb"].includes(partOfSpeech);
    const showGrammarSection = isVerb || showCaseField || showNumberField || showGenderField || showDegreeField;

    // Punctuation list based on language
    const punctuationList = lang === 'hebrew' ? HEBREW_PUNCTUATION : GREEK_PUNCTUATION;

    const formContent = (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            initialValues={{
                "book": ToTitleCase(book.match(/[^\d]+|\d+/g)?.join(' ')),
                "chapter": chapter,
                "verse": verse,
                "position": verses.length > 0 ? verses[verses.length - 1].position + 1 : 1,
                "part_of_speech": partOfSpeech,
            }}
        >
            {/* Quick Punctuation Panel - shown when current position is punctuation */}
            {isCurrentPositionPunctuation && !editId && (
                <Card
                    size="small"
                    style={{
                        marginBottom: 16,
                        background: 'linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)',
                        border: '1px solid #91d5ff'
                    }}
                >
                    <div style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, color: '#1890ff' }}>
                            Position {effectivePosition} is punctuation
                        </span>
                        {expectedPunctuation && (
                            <span style={{ marginLeft: 12, color: '#666' }}>
                                Expected: <span style={{
                                    fontSize: 18,
                                    fontFamily: lang === 'hebrew' ? 'SBL Hebrew, serif' : 'SBL Greek, serif',
                                    fontWeight: 'bold',
                                    color: '#1890ff',
                                    padding: '2px 8px',
                                    background: '#fff',
                                    borderRadius: 4,
                                    border: '1px solid #91d5ff'
                                }}>{expectedPunctuation}</span>
                            </span>
                        )}
                    </div>
                    <Space wrap>
                        {expectedPunctuation && (
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => handleQuickAddPunctuation(expectedPunctuation)}
                                style={{ fontFamily: lang === 'hebrew' ? 'SBL Hebrew, serif' : 'SBL Greek, serif' }}
                            >
                                Add "{expectedPunctuation}"
                            </Button>
                        )}
                        <span style={{ color: '#999', fontSize: 12 }}>or choose:</span>
                        {punctuationList.map(p => (
                            <Button
                                key={p}
                                size="small"
                                onClick={() => handleQuickAddPunctuation(p)}
                                style={{
                                    fontFamily: lang === 'hebrew' ? 'SBL Hebrew, serif' : 'SBL Greek, serif',
                                    minWidth: 32
                                }}
                            >
                                {p}
                            </Button>
                        ))}
                    </Space>
                </Card>
            )}

            {/* Word and Definition Row */}
            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item label="Word" style={{ marginBottom: 8 }}>
                        <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="word" noStyle>
                                {lang === 'greek'
                                    ? <Input
                                        onChange={handleGreek}
                                        onKeyDown={handleGreekKeyDown}
                                        onFocus={(e) => handleTranslatableInputFocus(e, 'word')}
                                        onSelect={handleTranslatableInputSelect}
                                        className={lang}
                                        style={{ width: 'calc(100% - 70px)' }}
                                        autoComplete="off"
                                      />
                                    : <Input
                                        onChange={handleHebrew}
                                        onKeyDown={handleHebrewKeyDown}
                                        onFocus={(e) => handleTranslatableInputFocus(e, 'word')}
                                        onSelect={handleTranslatableInputSelect}
                                        className={lang}
                                        style={{ width: 'calc(100% - 70px)' }}
                                        autoComplete="off"
                                      />
                                }
                            </Form.Item>
                            <Button onClick={handleCheckWord}>Check</Button>
                        </Space.Compact>
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item name="definition_in_context" label="Definition (in context)" rules={required}>
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            {/* Lexical Form and Gloss Row */}
            <Row gutter={16}>
                <Col xs={24} sm={12}>
                    <Form.Item name="lexical_form" label="Lexical Form" rules={required}>
                        {lang === 'greek'
                            ? <Input
                                onChange={handleGreek}
                                onKeyDown={handleGreekKeyDown}
                                onBlur={handleLexicalFormBlur}
                                onFocus={(e) => handleTranslatableInputFocus(e, 'lexical_form')}
                                onSelect={handleTranslatableInputSelect}
                                className={lang}
                                autoComplete="off"
                              />
                            : <Input
                                onChange={handleHebrew}
                                onKeyDown={handleHebrewKeyDown}
                                onBlur={handleLexicalFormBlur}
                                onFocus={(e) => handleTranslatableInputFocus(e, 'lexical_form')}
                                onSelect={handleTranslatableInputSelect}
                                className={lang}
                                autoComplete="off"
                              />
                        }
                    </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                    <Form.Item name="gloss" label="Gloss" rules={required}>
                        <Input />
                    </Form.Item>
                </Col>
            </Row>

            {/* Keyboard Toggle */}
            <div style={{ marginBottom: 12, textAlign: 'right' }}>
                <Button
                    type={showKeyboard ? 'primary' : 'default'}
                    icon={<FontSizeOutlined />}
                    onClick={() => setShowKeyboard(!showKeyboard)}
                    size="small"
                >
                    {showKeyboard ? 'Hide' : 'Show'} Keyboard Reference
                </Button>
            </div>

            {/* Keyboard Reference */}
            {showKeyboard && (
                <KeyboardReference
                    lang={lang}
                    onClose={() => setShowKeyboard(false)}
                    onCharacterClick={handleKeyboardCharacterClick}
                />
            )}

            {/* Part of Speech */}
            <Form.Item name="part_of_speech" label="Part of Speech" rules={required}>
                <Radio.Group onChange={handleRadioChange} size="small">
                    {parsingLOT.partsOfSpeech.map(p =>
                        <Radio.Button key={p} value={p.replaceAll(' ', '_')} style={{ marginBottom: 4 }}>
                            {p.split(' ').map(word =>
                                word.charAt(0).toUpperCase() + word.slice(1)
                            ).join(' ').replaceAll('Or', 'or')}
                        </Radio.Button>
                    )}
                </Radio.Group>
            </Form.Item>

            {/* Compact Grammar Section */}
            {showGrammarSection && (
                <Card size="small" title="Grammar" style={{ marginBottom: 16 }}>
                    <Row gutter={[12, 0]}>
                        {/* Hebrew verb fields: stem, tense, person, gender, number */}
                        {showHebrewVerbFields && (
                            <>
                                <Col xs={12} sm={8} md={6}>
                                    <Form.Item name="stem" label="Stem" style={{ marginBottom: 8 }}>
                                        <Select allowClear placeholder="Select">
                                            {buildSelects(parsingLOT.hebrewStem)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6}>
                                    <Form.Item name="tense" label="Tense" style={{ marginBottom: 8 }}>
                                        <Select allowClear placeholder="Select">
                                            {buildSelects(parsingLOT.tense)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6}>
                                    <Form.Item name="person" label="Person" style={{ marginBottom: 8 }}>
                                        <Select allowClear placeholder="Select">
                                            {buildSelects(parsingLOT.personAndDeclension)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                        {/* Greek verb fields: person, tense, voice, mood */}
                        {showGreekVerbFields && (
                            <>
                                <Col xs={12} sm={8} md={6}>
                                    <Form.Item name="person" label="Person" style={{ marginBottom: 8 }}>
                                        <Select allowClear placeholder="Select">
                                            {buildSelects(parsingLOT.personAndDeclension)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6}>
                                    <Form.Item name="tense" label="Tense" style={{ marginBottom: 8 }}>
                                        <Select allowClear placeholder="Select">
                                            {buildSelects(parsingLOT.tense)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6}>
                                    <Form.Item name="voice" label="Voice" style={{ marginBottom: 8 }}>
                                        <Select allowClear placeholder="Select">
                                            {buildSelects(parsingLOT.voice)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                                <Col xs={12} sm={8} md={6}>
                                    <Form.Item name="mood" label="Mood" style={{ marginBottom: 8 }}>
                                        <Select allowClear placeholder="Select">
                                            {buildSelects(parsingLOT.mood)}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                        {showCaseField && (
                            <Col xs={12} sm={8} md={6}>
                                <Form.Item name="case" label="Case" style={{ marginBottom: 8 }}>
                                    <Select allowClear placeholder="Select">
                                        {buildSelects(parsingLOT.case)}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                        {showGenderField && (
                            <Col xs={12} sm={8} md={6}>
                                <Form.Item name="gender" label="Gender" style={{ marginBottom: 8 }}>
                                    <Select allowClear placeholder="Select">
                                        {buildSelects(parsingLOT.gender)}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                        {showNumberField && (
                            <Col xs={12} sm={8} md={6}>
                                <Form.Item name="number" label="Number" style={{ marginBottom: 8 }}>
                                    <Select allowClear placeholder="Select">
                                        {buildSelects(parsingLOT.number)}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                        {showDegreeField && (
                            <Col xs={12} sm={8} md={6}>
                                <Form.Item name="degree" label="Degree" style={{ marginBottom: 8 }}>
                                    <Select allowClear placeholder="Select">
                                        {buildSelects(parsingLOT.degree)}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                </Card>
            )}

            {/* Location Row */}
            <Card size="small" title="Location" style={{ marginBottom: 16 }}>
                <Row gutter={12}>
                    <Col xs={12} sm={6}>
                        <Form.Item label="Book" style={{ marginBottom: 8 }}>
                            <Input disabled value={getBookDisplayName(book)} />
                            <Form.Item name="book" hidden><Input /></Form.Item>
                        </Form.Item>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Form.Item name="chapter" label="Chapter" rules={required} style={{ marginBottom: 8 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Form.Item name="verse" label="Verse" rules={required} style={{ marginBottom: 8 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Form.Item name="position" label="Position" rules={required} style={{ marginBottom: 8 }}>
                            <InputNumber
                                style={{ width: '100%' }}
                                onChange={(val) => {
                                    const pos = val as number;
                                    setCurrentPosition(pos);
                                    if (onPositionChange) onPositionChange(pos);
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Card>

            {/* Audio Recording - show when there's a word entered */}
            {currentWord && (
                <Card size="small" title="Audio Recording" style={{ marginBottom: 16 }}>
                    <AudioRecorder language={lang} word={currentWord} />
                </Card>
            )}

            {/* Submit Buttons */}
            <div style={{ marginTop: 16, textAlign: 'center' }}>
                {isPositionExceeded && !editId && (
                    <div style={{ color: '#ff4d4f', marginBottom: 8 }}>
                        Position {currentPosition} exceeds the exemplar ({maxPosition} words in this verse).
                    </div>
                )}
                <Space>
                    <Button type="primary" htmlType="submit" size="large" disabled={isPositionExceeded && !editId}>
                        {editId ? 'Update Word' : 'Add Word'}
                    </Button>
                    {(editId || isModal) && (
                        <Button size="large" onClick={handleCancel}>
                            Cancel
                        </Button>
                    )}
                </Space>
            </div>

            {/* Lexical Form Selection Modal */}
            <Modal
                title="Select Word Variant"
                open={isLexicalModalVisible}
                onCancel={() => { setIsLexicalModalVisible(false); setLexicalMatches([]); }}
                footer={null}
                width={800}
            >
                <p>Multiple words share this lexical form. Select one to populate the form:</p>
                <Table
                    dataSource={lexicalMatches}
                    rowKey="word"
                    size="small"
                    pagination={false}
                    onRow={(record) => ({
                        onClick: () => handleMatchSelection(record),
                        style: { cursor: 'pointer' }
                    })}
                    columns={[
                        { title: 'Word', dataIndex: 'word', key: 'word', render: (text: string) => <span className={lang}>{text}</span> },
                        { title: 'Gloss', dataIndex: 'gloss', key: 'gloss' },
                        { title: 'Part of Speech', dataIndex: 'part_of_speech', key: 'part_of_speech' },
                        { title: 'Number', dataIndex: 'number', key: 'number' },
                        { title: 'Gender', dataIndex: 'gender', key: 'gender' },
                        { title: 'Case', dataIndex: 'case', key: 'case' },
                    ]}
                />
            </Modal>
        </Form>
    );

    return formContent;
}
